import shoelaceStyles from '../styles/shoelaceStyles';
import styles from '../styles/styles.scss';
import {when} from 'lit/directives/when.js';
import {css, html, LitElement, PropertyValues, TemplateResult} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {map} from 'lit/directives/map.js';
import {Site} from '../models/Site';
import {ConfigurationState} from '../storage/ConfigurationState';
import {Feature} from 'geojson';
import {log} from '../controllers/LogController';

// prevent tree shaking
export {};

@customElement('ca-list')
export class CaListElement extends LitElement {
  static override styles = [
    shoelaceStyles,
    styles,
    css`
      :host {
        display: block;
      }

      ul[data-ca-role="list"] {
        list-style: none;
        padding: 0;
      }

      ul[data-ca-role="list"] > li {
        background: var(--sl-color-neutral-0);
        transition: background .3s, color .3s;
        margin-bottom: 1em;
      }

      ul[data-ca-role="list"] > li.active,
      ul[data-ca-role="list"] > li:hover {
        background: var(--ca-color-highlight);
        color: #fff;
      }

      ul[data-ca-role="list"] > li > a {
        color: inherit;
        text-decoration: none;
        padding: 1em;
        display: flex;
        justify-content: space-between;
        gap: 1em;
      }

      ul[data-ca-role="list"] > li > a > div {
        display: flex;
        flex-flow: column;
        justify-content: space-between;
        gap: 1em;
      }

      ul[data-ca-role="list"] > li h2 {
        color: var(--ca-color-primary);
        margin-bottom: 0;
        line-height: 1.3;
      }

      ul[data-ca-role="list"] > li.active h2,
      ul[data-ca-role="list"] > li:hover h2 {
        color: #fff;
      }

      ul[data-ca-role="list"] > li h2 + div {
        color: var(--ca-color-primary);
      }

      ul[data-ca-role="list"] > li.active h2 + div,
      ul[data-ca-role="list"] > li:hover h2 + div {
        color: #fff;
      }

      ul[data-ca-role="list"] > li .description {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
      }

      @media (max-width: 560px) {
        ul[data-ca-role="list"] > li > a {
          flex-flow: column;
        }
      }
    `,
  ];

  @property()
  featuresPerPage = 6;

  @state()
  shownFeatures = this.featuresPerPage;

  @property({type: Object})
  selectedFeature: Feature | null = null;

  @property({type: Object})
  features: Feature[] = [];

  override render() {
    return [
      when(
        this.features,
        () => when(
          this.features.length > 0,
          () => [
            html`
              <div>${this.features.length} Treffer</div>
              <ul data-ca-role="list">
                ${map(
                  this.features.slice(0, this.shownFeatures),
                  (feature) => this._renderFeature(feature),
                )}
              </ul>
            `,
            when(
              this.features.length > this.shownFeatures,
              () =>
                html`
                  <div style="text-align: center">
                    <sl-button
                      @click=${() => {
                        this.shownFeatures += this.featuresPerPage;
                      }}
                      title="Mehr anzeigen"
                      style="--sl-input-border-radius-medium: 15px"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="29.698" height="42.002"
                           viewBox="0 0 29.698 42.002"
                           style="color: var(--ca-color-primary); vertical-align: middle; margin: 10px 10px 10px 0"
                      >
                        <path d="M35.5,16h-7V33.5h7ZM17.152,43.153,32,58,46.851,43.153,41.9,38.2,32,48.1l-9.9-9.9Z"
                              transform="translate(-17.152 -16)" fill="currentColor" />
                      </svg>
                      Weitere Treffer
                    </sl-button>
                  </div>
                `,
            ),
          ],
          () => html`
            <p>
              Keine Treffer
            </p>
          `,
        ),
        () => html`
          <sl-spinner
            style="font-size: 50px; --track-width: 5px; margin: 1em auto; display: block;"
          ></sl-spinner>
        `,
      ),
    ];
  }

  override willUpdate(changedProperties: PropertyValues) {
    log(this.constructor.name, 'willUpdate', changedProperties, changedProperties.get('features'));
    // set to first page when new results arrived
    // we don't check for equality here, because the array reference might change
    if (
      changedProperties.has('features')
      && (<Feature[]>changedProperties.get('features'))?.length !== this.features?.length
    ) {
      log(this.constructor.name, 'willUpdate', 'features have been updated', changedProperties);
      this.shownFeatures = this.featuresPerPage;
    }
  }

  private _renderFeature(feature: Feature): TemplateResult {
    return html`
      <li class=${classMap({active: feature === this.selectedFeature})}>
        <a href=""
           @click=${(ev: Event) => {
             this.dispatchEvent(
               new CustomEvent(
                 'ca-feature-selected',
                 {
                   detail: feature,
                   bubbles: true,
                   composed: true,
                 },
               ),
             );
             ev.preventDefault();
           }}
        >
          <div>
            <div>
              <h2>
                ${feature.properties?.company}
              </h2>
              <div>
                ${Site.urlDomain(feature.properties?.site_url)}
              </div>
            </div>
            <p class="description">
              ${feature.properties?.description}
            </p>
            <div style="font-size: 90%">
              <svg style="height: 1em; vertical-align: middle" xmlns="http://www.w3.org/2000/svg" width="20.711"
                   height="25" viewBox="0 0 20.711 25">
                <path
                  d="M13.355,23.746l5.7-5.7a8.054,8.054,0,1,0-11.39,0Zm0,3.254L6.033,19.678a10.355,10.355,0,1,1,14.645,0Zm0-12.343a2.3,2.3,0,1,0-2.3-2.3A2.3,2.3,0,0,0,13.355,14.657Zm0,2.3a4.6,4.6,0,1,1,4.6-4.6A4.6,4.6,0,0,1,13.355,16.958Z"
                  transform="translate(-3 -2)" fill="currentColor" />
              </svg>
              ${feature.properties?.zip} ${feature.properties?.city}
            </div>
          </div>
          <div style="flex-basis: calc(min(40%, 414px)); flex-shrink: 0">
            ${when(
              feature!.properties?.hasImage || feature!.properties?.screenshot_path,
              () => html`
                <img src="${ConfigurationState.siteImgUrl(feature!, 'w480')}"
                     alt="Screenshot ${feature?.properties?.company}"
                     loading="lazy"
                     style="max-width: 100%"
                >
              `,
            )}
          </div>
        </a>
      </li>
    `;
  }
}
